import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import type { ProjectDto, ProjectPermission } from '@project-os/contracts';
import { useViewerMode } from './viewer-mode';

export interface ProjectOutletContext {
  project: ProjectDto;
}

/**
 * Текущий проект, загруженный оболочкой ProjectLayout.
 *
 * Права приходят с сервера, но проходят через гостевой просмотр: когда он
 * включён, из набора остаются только права наблюдателя. Фильтр стоит здесь,
 * а не в каждой странице, — иначе один забытый вызов показывал бы кнопку,
 * которой у наблюдателя нет, и режим врал бы ровно там, где его проверяют.
 */
export function useProjectContext(): ProjectDto {
  const { project } = useOutletContext<ProjectOutletContext>();
  const viewerMode = useViewerMode();

  return useMemo(
    () =>
      viewerMode.active
        ? { ...project, permissions: viewerMode.filter(project.permissions) }
        : project,
    [project, viewerMode],
  );
}

/** Настоящий проект без учёта гостевого просмотра. */
export function useRealProjectContext(): ProjectDto {
  return useOutletContext<ProjectOutletContext>().project;
}

/**
 * Есть ли у текущего пользователя право в этом проекте.
 * Права приходят с сервера вместе с проектом — интерфейс их не вычисляет.
 */
export function useProjectPermission(permission: ProjectPermission): boolean {
  const project = useProjectContext();
  return project.permissions.includes(permission);
}
